import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createSelector } from 'reselect';
// eslint-disable-next-line no-restricted-imports
import { Table } from 'semantic-ui-react';
import { routes } from '../../../../config/routes';

import { setOrganizationInvoicePlan } from '../../../../api/firebase/invoices';
import { invoiceEditSelected } from '../../../../redux/actions/billingActions';
import { logError } from '../../../../rollbar';

import { getPrimaryLocationTimezone } from '../../../../helpers/invoicePlans';
import { useLoadedStudents } from '../../../../students/studentsHooks';
import { createArrayOfFamilyContacts } from '../../../../students/studentsUtils';

// Import style
import { InvoicePlan, InvoicePlanStatus } from '@wonderschool/common-base-types';
import { Card, InformationalBanner, showToast, ToastTypeEnum } from '@wonderschool/common-base-ui';
import DateTime from '../../../DateTime/DateTime';
import '../InvoiceDetail/InvoiceDetail.scss';
import { InvoicePlanActionButtons } from './InvoicePlanActionButtons';
import { InvoicePlanDetails } from './InvoicePlanDetails';
import { InvoicePlanStatusLabel } from './InvoicePlanStatusLabel';
import { InvoicePlanSummary } from './InvoicePlanSummary';

const selectSelectedRecurringInvoices = createSelector(
  [(state) => state.invoices],
  (invoices) => invoices?.recurring?.selected
);
const selectCurrentOrganization = createSelector(
  [(state) => state.organizations],
  (organizations) => organizations?.currentOrganization
);

type InvoicePlanWithIntervalRecovery = InvoicePlan & {
  intervalTransitionError?: string;
  intervalTransitionRecovery?: { attemptId?: string; publicErrorCode?: string };
};

type InvoicePlanDetailsContainerProps = {
  invoicePlan: InvoicePlan | null;
  showActionButtons?: boolean;
  showProjectedCharges?: boolean;
  onPlanUpdated?: (invoicePlan: InvoicePlan) => void;
};

export function InvoicePlanDetailsContainer({
  invoicePlan,
  showActionButtons = true,
  showProjectedCharges = false,
  onPlanUpdated,
}: InvoicePlanDetailsContainerProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const invoicePlanFromStore: InvoicePlan | null = useSelector(selectSelectedRecurringInvoices);
  const { id: organizationId } = useSelector(selectCurrentOrganization);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const planTimezone = useSelector((state: any) => getPrimaryLocationTimezone(state.locations?.list));
  const [isSendingInvoicePlan, setIsSendingInvoicePlan] = useState(false);
  const isSendingInvoicePlanRef = useRef(false);
  const [isEditingInvoicePlan, setIsEditInvoicePlan] = useState(false);

  const sourceInvoicePlan: InvoicePlanWithIntervalRecovery | null = invoicePlan || invoicePlanFromStore;
  const sourceInvoicePlanIdRef = useRef(sourceInvoicePlan?.id);
  sourceInvoicePlanIdRef.current = sourceInvoicePlan?.id;
  const [selectedInvoicePlan, setSelectedInvoicePlan] = useState<InvoicePlanWithIntervalRecovery | null>(
    sourceInvoicePlan
  );

  useEffect(() => {
    setSelectedInvoicePlan(sourceInvoicePlan);
  }, [sourceInvoicePlan]);

  const students = useLoadedStudents(
    (selectedInvoicePlan?.students && selectedInvoicePlan.students.map((student) => student.id)) || []
  );
  const responsibleForBilling = students
    .map((student) => {
      if (student.responsibleForBilling) return student.responsibleForBilling;

      const families = createArrayOfFamilyContacts(student);

      if (families.length <= 0) {
        return null;
      }

      const familyMember = families.find((parent) => !!parent.email);

      if (!familyMember) {
        return null;
      }

      const data = {
        displayName: familyMember.displayName,
        email: familyMember.email,
      };
      return data;
    })
    .filter((responsible) => !!responsible);

  if (responsibleForBilling.length <= 0)
    responsibleForBilling.push({
      displayName: `${t('billing.invoiceDetail.fallback.noParent')} :(`,
      email: `${t('billing.invoiceDetail.fallback.noEmail')} :(`,
    });

  useEffect(() => {
    if (isEditingInvoicePlan) {
      navigate(routes.billing.plans.edit);
    }
  }, [isEditingInvoicePlan, navigate]);

  const onClickEdit = useCallback(() => {
    dispatch(invoiceEditSelected(selectedInvoicePlan));
    setIsEditInvoicePlan(true);
  }, [dispatch, selectedInvoicePlan]);

  const handleChangeStatus = useCallback(
    async (newStatus: InvoicePlanStatus, updates: Partial<InvoicePlan> = {}) => {
      if (selectedInvoicePlan && !isSendingInvoicePlanRef.current) {
        isSendingInvoicePlanRef.current = true;
        setIsSendingInvoicePlan(true);
        const updatedInvoicePlan = { ...selectedInvoicePlan, ...updates, status: newStatus };
        try {
          await setOrganizationInvoicePlan(organizationId, updatedInvoicePlan);
          onPlanUpdated?.(updatedInvoicePlan);
          if (sourceInvoicePlanIdRef.current === updatedInvoicePlan.id) {
            setSelectedInvoicePlan(updatedInvoicePlan);
          }
        } catch (error) {
          logError('InvoicePlanDetailsContainer: failed saving invoice plan status', error);
          showToast(ToastTypeEnum.Error, t('billing.invoicePlan.saveFailed'));
        } finally {
          isSendingInvoicePlanRef.current = false;
          setIsSendingInvoicePlan(false);
        }
      }
    },
    [onPlanUpdated, organizationId, selectedInvoicePlan, t]
  );

  return (
    <div className="flex flex-col gap-4">
      {selectedInvoicePlan?.intervalTransitionRecovery && (
        <InformationalBanner
          type="error"
          data-testid="interval-transition-recovery"
          label={t('billing.intervalChange.recoveryNeeded', {
            reason: t(
              selectedInvoicePlan.intervalTransitionRecovery.publicErrorCode === 'cadence_transition_failed'
                ? 'billing.intervalChange.recoveryReason.cadence_transition_failed'
                : 'billing.intervalChange.recoveryReason.generic'
            ),
          })}
        />
      )}
      <div className="flex w-full flex-row justify-between">
        <div>
          <InvoicePlanStatusLabel status={selectedInvoicePlan?.status} data-testid="invoice-plan-detail-status" />
        </div>

        <div className="ml-auto mr-0 flex">
          {selectedInvoicePlan && (
            <InvoicePlanActionButtons
              showActionButtons={showActionButtons}
              loading={isSendingInvoicePlan}
              selectedInvoicePlan={selectedInvoicePlan}
              onChangeStatus={handleChangeStatus}
              onClickEdit={onClickEdit}
            />
          )}
        </div>
      </div>
      <InvoicePlanSummary
        selectedInvoicePlan={selectedInvoicePlan}
        responsibleForBilling={responsibleForBilling}
        organization={currentOrganization}
        showProjectedCharges={showProjectedCharges}
        timezone={planTimezone}
      />

      <InvoicePlanDetails selectedInvoicePlan={selectedInvoicePlan} responsibleForBilling={responsibleForBilling} />
      {(selectedInvoicePlan?.invoiceItemList?.length ?? 0) > 0 && (
        <SentInvoices selectedInvoicePlan={selectedInvoicePlan} />
      )}
    </div>
  );

  function SentInvoices({ selectedInvoicePlan }) {
    const invoices = selectedInvoicePlan?.invoices?.map((invoice) => {
      return (
        <Table.Row key={invoice.id}>
          <Table.Cell data-testid="invoice-plan-invoice-id">{invoice.id}</Table.Cell>
          <Table.Cell data-testid="invoice-plan-invoice-status">{invoice.status}</Table.Cell>
          <Table.Cell data-testid="invoice-plan-invoice-created-at">
            {invoice.createdAt && <DateTime epoch={invoice.createdAt} format={'MM/DD/YYYY'} />}
          </Table.Cell>
          <Table.Cell data-testid="invoice-plan-invoice-total">{invoice.total}</Table.Cell>
        </Table.Row>
      );
    });
    return (
      <Card
        title={
          <div className="p-2 text-3xl font-bold text-black" data-testid="invoice-plan-detail-invoices-header">
            {t('billing.invoicePlan.invoices.heading')}
          </div>
        }
      >
        <div className="flex flex-col gap-4 p-4">
          <Table singleLine basic="very">
            <Table.Body>
              <Table.Row>
                <Table.HeaderCell data-testid="invoice-plan-invoice-id">
                  {t('billing.invoicePlan.invoices.invoiceIdColumn')}
                </Table.HeaderCell>
                <Table.HeaderCell data-testid="invoice-plan-invoice-status">{t('common.status')}</Table.HeaderCell>
                <Table.HeaderCell data-testid="invoice-plan-invoice-created-at">
                  {t('billing.invoicePlan.invoices.createdOnColumn')}
                </Table.HeaderCell>
                <Table.HeaderCell data-testid="invoice-plan-invoice-total">
                  {t('billing.invoicePlan.invoices.totalColumn')}
                </Table.HeaderCell>
              </Table.Row>

              {invoices ? (
                invoices
              ) : (
                <Table.Row key={'noInvoices'} textAlign="center">
                  <Table.Cell colSpan={4} data-testid="invoice-plan-no-invoices">
                    {t('billing.invoicePlan.invoices.emptyMessage')}
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      </Card>
    );
  }
}

export default InvoicePlanDetailsContainer;
